import type { Disponibilidad, NegocioNuevo } from '../api/ops/negocio';

/**
 * Reglas de «Tu negocio» (1.5, `diseno/plataforma/1.1-1.6-1.10-alta-e-inicio-de-sesion.html`): listas de país, moneda y
 * zona horaria del dibujo (el país rellena moneda y zona, que se pueden cambiar), borrador en este dispositivo hasta
 * crearlo (G169), dirección web comprobada al escribir (G180, G216) y lo que se manda en `createBusiness` (G170, G201,
 * G980).
 */

/** País → moneda y zona horaria por defecto (se pueden cambiar). */
export const PAISES_ALTA: readonly (readonly [string, string, string, string])[] = [
    ['ES', 'España', 'EUR', 'Europe/Madrid'], ['PT', 'Portugal', 'EUR', 'Europe/Lisbon'], ['AD', 'Andorra', 'EUR', 'Europe/Andorra'],
    ['FR', 'Francia', 'EUR', 'Europe/Paris'], ['IT', 'Italia', 'EUR', 'Europe/Rome'], ['DE', 'Alemania', 'EUR', 'Europe/Berlin'],
    ['IE', 'Irlanda', 'EUR', 'Europe/Dublin'], ['GB', 'Reino Unido', 'GBP', 'Europe/London'], ['MX', 'México', 'MXN', 'America/Mexico_City'],
    ['AR', 'Argentina', 'ARS', 'America/Argentina/Buenos_Aires'], ['CO', 'Colombia', 'COP', 'America/Bogota'], ['CL', 'Chile', 'CLP', 'America/Santiago'],
];

export const MONEDAS_ALTA: readonly (readonly [string, string])[] = [
    ['EUR', 'Euro (€)'], ['GBP', 'Libra esterlina (£)'], ['USD', 'Dólar estadounidense ($)'], ['MXN', 'Peso mexicano ($)'], ['ARS', 'Peso argentino ($)'],
    ['COP', 'Peso colombiano ($)'], ['CLP', 'Peso chileno ($)'], ['CHF', 'Franco suizo (CHF)'], ['DKK', 'Corona danesa (kr)'],
];

export const ZONAS_ALTA: readonly (readonly [string, string])[] = [
    ['Europe/Madrid', 'Madrid, península y Baleares'], ['Atlantic/Canary', 'Canarias'], ['Africa/Ceuta', 'Ceuta y Melilla'], ['Europe/Lisbon', 'Lisboa'],
    ['Atlantic/Azores', 'Azores'], ['Europe/Andorra', 'Andorra'], ['Europe/Paris', 'París'], ['Europe/Rome', 'Roma'], ['Europe/Berlin', 'Berlín'],
    ['Europe/Dublin', 'Dublín'], ['Europe/London', 'Londres'], ['America/Mexico_City', 'Ciudad de México'], ['America/Argentina/Buenos_Aires', 'Buenos Aires'],
    ['America/Bogota', 'Bogotá'], ['America/Santiago', 'Santiago de Chile'],
];

/** «Qué vas a gestionar» (G170): `manages` de createBusiness. */
export const GESTIONA: readonly (readonly [string, NonNullable<NegocioNuevo['manages']>])[] = [
    ['Reservas', ['reservas']],
    ['Citas', ['citas']],
    ['Las dos', ['reservas', 'citas']],
];

export interface BorradorAlta {
    nombre: string;
    gestiona: string;
    sector: string;
    slug: string;
    pais: string;
    moneda: string;
    zona: string;
    partner: string;
}

export const BORRADOR_VACIO: BorradorAlta = { nombre: '', gestiona: '', sector: '', slug: '', pais: 'ES', moneda: 'EUR', zona: 'Europe/Madrid', partner: '' };

/** Lo escrito en «Tu negocio» se guarda en este navegador hasta crear el negocio (G169). */
export const CLAVE_BORRADOR = 'rv-alta-negocio';

export function leerBorrador(): BorradorAlta {
    try {
        const crudo = window.localStorage.getItem(CLAVE_BORRADOR);
        const guardado: unknown = crudo === null ? null : JSON.parse(crudo);
        if (guardado === null || typeof guardado !== 'object') {
            return BORRADOR_VACIO;
        }
        const g = guardado as Partial<Record<keyof BorradorAlta, unknown>>;
        const texto = (k: keyof BorradorAlta) => (typeof g[k] === 'string' ? g[k] : BORRADOR_VACIO[k]);
        return { nombre: texto('nombre'), gestiona: texto('gestiona'), sector: texto('sector'), slug: texto('slug'), pais: texto('pais'), moneda: texto('moneda'), zona: texto('zona'), partner: texto('partner') };
    } catch {
        return BORRADOR_VACIO;
    }
}

/**
 * El enlace de alta de un partner (1.23) lleva su código (`/crear-cuenta?p=…`): se recuerda en el borrador para que «Tu
 * negocio» (1.5) lo mande como `partner_code`, con el campo ya abierto.
 */
export function recordarCodigoPartner(codigo: string): void {
    const limpio = codigo.trim().toUpperCase();
    if (limpio !== '') {
        guardarBorrador({ ...leerBorrador(), partner: limpio });
    }
}

export function guardarBorrador(b: BorradorAlta): void {
    try {
        window.localStorage.setItem(CLAVE_BORRADOR, JSON.stringify(b));
    } catch {
        // Sin almacenamiento (navegación privada): el alta sigue, solo no se recuerda.
    }
}

export function borrarBorrador(): void {
    try {
        window.localStorage.removeItem(CLAVE_BORRADOR);
    } catch {
        // Nada que borrar.
    }
}

/** La dirección propuesta desde el nombre: minúsculas, sin tildes, guiones en lugar de lo demás (como el dibujo). */
export function slugDeNombre(nombre: string): string {
    return nombre.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
}

/** Lo que se escribe en la dirección: minúsculas sin tildes; lo que no vale, guion (sin quitar el del final al escribir). */
export function slugEscrito(valor: string): string {
    return valor.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9-]+/g, '-').replace(/-{2,}/g, '-').replace(/^-+/, '').slice(0, 50);
}

const FORMATO = /^[a-z0-9](?:[a-z0-9-]{1,48}[a-z0-9])$/;

export const TEXTOS_ALTA = {
    titulo: 'Tu negocio',
    sub: 'Empieza tu prueba gratuita de 14 días.',
    crear: 'Crear mi negocio',
    creando: 'Creando tu negocio…',
    nota: 'Se guarda en este dispositivo hasta que lo crees.',
    conPartner: '¿Tienes un código de partner?',
    sinPartner: 'No tengo código de partner',
    ayudaPartner: 'Te lo da una agencia o un partner de Reservaria.',
    robot: 'No hemos podido comprobar que no eres un robot. Vuelve a intentarlo.',
    nombre: 'Escribe el nombre de tu negocio.',
    gestiona: 'Elige una opción.',
    sector: 'Elige uno.',
    slugVacio: 'Elige la dirección de tu web.',
    slugAyuda: 'De 3 a 50: minúsculas, números y guiones.',
    slugLargo: 'Tiene que tener de 3 a 50 caracteres.',
    slugFormato: 'Formato no válido: usa minúsculas sin tilde, números y guiones (de 3 a 50).',
    slugReservada: 'Esta dirección está reservada. Prueba otra.',
    slugOcupada: 'Esta dirección ya la usa otro negocio.',
    slugComprobando: 'Comprobando…',
    // «Libre: tu web será …» con la dirección que da checkSlugAvailability (`public_url`, G990, ADR-0252): el panel no
    // conoce el dominio de cada entorno (G118) y no la arma; se enseña sin `https://`, como el dibujo.
    slugLibre: (direccion: string) => `Libre: tu web será ${direccion}`,
    // Solo mientras no ha llegado la lista de sectores (con el anexo, que siempre viene, G991).
    sinAnexo: 'Ahora mismo no se puede crear el negocio. Prueba de nuevo en un rato.',
} as const;

export interface EstadoDireccion {
    ok: boolean;
    ayuda?: string;
    error?: string;
}

/** La dirección: primero lo que se ve sin preguntar (largo y formato) y luego la respuesta de checkSlugAvailability. */
export function estadoDireccion(slug: string, comprobando: boolean, respuesta: Disponibilidad | null): EstadoDireccion {
    if (slug === '') {
        return { ok: false, ayuda: TEXTOS_ALTA.slugAyuda };
    }
    if (slug.length < 3 || slug.length > 50) {
        return { ok: false, error: TEXTOS_ALTA.slugLargo };
    }
    if (!FORMATO.test(slug)) {
        return { ok: false, error: TEXTOS_ALTA.slugFormato };
    }
    if (comprobando || respuesta?.slug !== slug) {
        return { ok: false, ayuda: TEXTOS_ALTA.slugComprobando };
    }
    switch (respuesta.reason) {
        case 'reserved':
            return { ok: false, error: TEXTOS_ALTA.slugReservada };
        case 'taken':
            return { ok: false, error: TEXTOS_ALTA.slugOcupada };
        case 'invalid_format':
            return { ok: false, error: TEXTOS_ALTA.slugFormato };
        default:
            return { ok: true, ayuda: TEXTOS_ALTA.slugLibre(sinEsquema(respuesta.public_url ?? '')) };
    }
}

/** Una dirección sin `https://` ni barra final, como la escribe el dibujo. */
export function sinEsquema(url: string): string {
    return url.replace(/^https?:\/\//, '').replace(/\/+$/, '');
}

/**
 * Lo que va delante del campo (el dominio del entorno con su barra): lo que precede al slug en la `public_url` de
 * la última respuesta de checkSlugAvailability (G990). Sin respuesta con dirección, no se sabe y no se pinta.
 */
export function prefijoDeDireccion(respuesta: Disponibilidad | null): string | null {
    if (respuesta === null || respuesta.public_url === null) {
        return null;
    }
    const limpia = sinEsquema(respuesta.public_url);
    const fin = `/${encodeURIComponent(respuesta.slug)}`;
    return limpia.endsWith(fin) ? limpia.slice(0, limpia.length - fin.length + 1) : null;
}

export const sePuedeComprobar = (slug: string): boolean => FORMATO.test(slug);

export type ErroresAlta = Partial<Record<'nombre' | 'gestiona' | 'sector' | 'slug', string>>;

export function validarAlta(b: BorradorAlta): ErroresAlta {
    const e: ErroresAlta = {};
    if (b.nombre.trim() === '') {
        e.nombre = TEXTOS_ALTA.nombre;
    }
    if (b.gestiona === '') {
        e.gestiona = TEXTOS_ALTA.gestiona;
    }
    if (b.sector === '') {
        e.sector = TEXTOS_ALTA.sector;
    }
    if (b.slug === '') {
        e.slug = TEXTOS_ALTA.slugVacio;
    } else if (b.slug.length < 3 || b.slug.length > 50) {
        e.slug = TEXTOS_ALTA.slugLargo;
    } else if (!FORMATO.test(b.slug)) {
        e.slug = TEXTOS_ALTA.slugFormato;
    }
    return e;
}

/** Lo que se manda en createBusiness, con la versión vigente del anexo que se ha enseñado (G980). */
export function datosDelAlta(b: BorradorAlta, versionAnexo: string): NegocioNuevo {
    const manages = GESTIONA.find(([t]) => t === b.gestiona)?.[1];
    const partner = b.partner.trim();
    return {
        name: b.nombre.trim(),
        sector: b.sector,
        country: b.pais,
        timezone: b.zona,
        currency: b.moneda,
        slug: b.slug,
        ...(manages === undefined ? {} : { manages: [...manages] }),
        ...(partner === '' ? {} : { partner_code: partner }),
        data_processing_terms_version: versionAnexo,
    };
}
